import { useSearch } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  getBrowserLanguage,
  LANG_STORAGE_KEY,
  SUPPORTED_LANGS,
  type SupportedLang,
} from "@/i18n";

const LANG_COOKIE = "munchi-lang";

function setLangCookie(lang: string) {
  // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API lacks full browser support
  document.cookie = `${LANG_COOKIE}=${lang};path=/;max-age=31536000;SameSite=Lax`;
}

export function useLanguage() {
  const { i18n } = useTranslation();
  const search = useSearch({ strict: false }) as { lang?: string };

  useEffect(() => {
    const isSupported = (v: string | null | undefined): v is SupportedLang =>
      SUPPORTED_LANGS.includes(v as SupportedLang);

    const stored =
      typeof localStorage !== "undefined"
        ? localStorage.getItem(LANG_STORAGE_KEY)
        : null;

    if (isSupported(stored)) {
      setLangCookie(stored);
      if (i18n.language !== stored) i18n.changeLanguage(stored);
      return;
    }

    const urlLang = search?.lang;
    const target = isSupported(urlLang) ? urlLang : getBrowserLanguage();
    if (isSupported(urlLang)) {
      localStorage.setItem(LANG_STORAGE_KEY, urlLang);
    }
    setLangCookie(target);
    if (i18n.language !== target) i18n.changeLanguage(target);
  }, [search?.lang, i18n]);

  function selectLanguage(lang: string) {
    localStorage.setItem(LANG_STORAGE_KEY, lang);
    setLangCookie(lang);
    i18n.changeLanguage(lang);
  }

  return {
    language: i18n.language,
    selectLanguage,
  };
}
